import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { UseQueryCustomOptions } from "app/types/application-types";
import SysColumn from "app/class/sys-column";
import { RecordManagement } from "app/class/record-management";
import { aggregateInheritedColumns, SysColumnOverride } from "app/hooks/columnInheritance";
import { useTables } from "app/hooks/useTables";
import metadataQueryKeys from "shared/services/metadataQueryKeys";

export const fetchColumns = () =>
    new RecordManagement<SysColumn>("sys_columns").paginate(1, 5000).orderBy("order", "asc").run();

export const useColumns = (options?: UseQueryCustomOptions) => {
    const tables = useTables(options?.enabled === false ? { enabled: false } : undefined);
    const query = useQuery({
        queryKey: metadataQueryKeys.columns,
        queryFn: fetchColumns,
        staleTime: 1000 * 60 * 5, // 5 minutes
        ...options
    });
    const overrides = useQuery({
        queryKey: ["sys_column_overrides", "all"],
        queryFn: async () => {
            const result = await new RecordManagement<SysColumnOverride>("sys_column_overrides").paginate(1, 5000).run();
            return Array.isArray(result?.data) ? result.data : [];
        },
        enabled: options?.enabled !== false && (tables.data ?? []).some((table) => Boolean(table.parent_table_name)),
        staleTime: 1000 * 60 * 5,
    });

    const data = useMemo(
        () => aggregateInheritedColumns(
            query.data?.data.map((item: any) => new SysColumn(item)) ?? [],
            tables.data ?? [],
            Array.isArray(overrides.data) ? overrides.data : []
        ),
        [query.data?.data, tables.data, overrides.data]
    );

    return {
        ...query,
        data,
        isLoading: query.isLoading || tables.isLoading || overrides.isLoading,
    };
};
